import React, { memo, useCallback } from "react";
import { cn } from "utils";
import { Icon, OspToast, deviceDetector, ospThemes } from "@ospweb/sdk-ui";
// import Image from "next/image";
import log from "logbeacon";
import { useSnapshot } from "valtio";
import { ProductStatus } from "../../../CurrentPlan/constants";

import { BillingCycle } from "../../constants";
import { PurchaseActionsProps } from "../../types";
import { getTrialData } from "../../../CurrentPlan/utils/getTrialData";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/Subscription/SubscriptionCard/PurchaseActions/index";
import { UpgradeReminderModalCreateAble } from "@/src/components/modal/UpgradeReminder";
import { rootStore } from "@/src/store";
import {
  useAppPurchaseFlow,
  useAppPurchaseMemberInvalidate,
  useLoginModalProvider,
  useShellRouter,
} from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 订阅计划购买操作组件
 * App 内购：产品列表由 GET /products 取得，购买前调 payment/check，购买成功后由 Web 调 confirm
 */
const PurchaseActions: React.FC<PurchaseActionsProps> = ({
  isFree,
  userPlanInfo,
  billingCycle,
  plan,
  onStripeClick,
  isLoading,
  isH5,
  trialList,
  onAppPurchaseSuccess,
  disableInvisiblePlaceholder = false,
  buttonTitleOverride,
  forceButtonDisabled,
  customCta,
  // hidePaymentIcons = false,
  showCryptoOverride,
  className,
}) => {
  const {
    auto_renewal,
    renewal_id,
    isFree: isUserFree,
    isTrial: isUserTrial,
    available_products,
  } = userPlanInfo ?? {};
  const { open } = useLoginModalProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const router = useShellRouter();
  const {
    beginAppPurchase,
    completeAppPurchase,
    invalidateMemberQueriesAfterDelay,
  } = useAppPurchaseMemberInvalidate();
  // 用户是否为会员
  const isMember = !isUserFree;
  const autoRenewal = auto_renewal;
  const isInIOS = deviceDetector.isIOSApp();
  // const isInApp = deviceDetector.isApp();

  const {
    purchaseInApp,
    isPurchasing: isAppPurchasing,
    shouldTryNativePayment,
  } = useAppPurchaseFlow();
  // 用户当前购买的是试用会员，并且自动续费的产品id和当前产品id相同
  const isPayingPlan = isUserTrial && renewal_id === plan?.id;
  // 当前生效的产品id相同，或者用户当前购买的是试用会员并且自动续费产品id与当前产品id相同，则属于同一个产品
  const isCurrentPlan = userPlanInfo?.product?.id === plan?.id || isPayingPlan;

  // 判断该正价产品是否有对应的试用产品
  const isTrialPlan = trialList?.some(
    (item) =>
      item.renewal_id === plan?.id && item.status === ProductStatus.ACTIVE,
  );
  const trialPrice = trialList?.find(
    (item) =>
      item.renewal_id === plan?.id && item.status === ProductStatus.ACTIVE,
  )?.price;

  // 禁用crypto支付 2.8.0 版本
  const showCryptoPayment = false;

  const buttonText = (): {
    buttonTitle: string;
    buttonDisabled: boolean;
    isUpgrade?: boolean;
  } => {
    if (!isLoggedIn) {
      return {
        buttonTitle: I18n.t("Create Account"),
        buttonDisabled: false,
        isUpgrade: false,
      };
    }
    // 当该相同周期的产品可以试用时
    if (!isMember && isTrialPlan) {
      // 非会员状态，展示为立即购买：
      // 点击购买判断是否登录，未登录弹窗登录弹窗，登录后
      // 如果试用会员，则展示为‘~Try $1 Trial~’，按钮可点击
      // 如果非试用会员，则展示为‘~Subscribe~’，按钮可点击
      return {
        buttonTitle: isInIOS
          ? I18n.t("Start with 7 days free trial")
          : I18n.t("Try 7 Days for %{var0}", { var0: `$${trialPrice}` }),
        buttonDisabled: false,
        isUpgrade: false,
      };
    }

    // 当前生效套餐：固定为 Current Plan，且自动续费时按钮禁用
    if (isCurrentPlan && autoRenewal && !isFree) {
      return {
        buttonTitle: I18n.t("Current Plan"),
        buttonDisabled: true,
        isUpgrade: false,
      };
    }

    // 其余情况：根据 available_products 决定按钮文案和状态
    const matchedAvailable = (available_products || []).find(
      (item: { product_id?: string }) => item.product_id === plan?.id,
    );

    if (!matchedAvailable) {
      // 没有匹配到可用产品，表示无法购买，统一为 Contact Us
      return {
        buttonTitle: I18n.t("Contact Us"),
        buttonDisabled: true,
        isUpgrade: true,
      };
    }

    // todo: 上线的时候，需要把升级的入口禁掉，后续升级逻辑一致之后再打开
    switch (matchedAvailable.action) {
      case "PURCHASE":
        return {
          buttonTitle:
            billingCycle === BillingCycle.LIFETIME
              ? I18n.t("Claim Founding Pass")
              : I18n.t("Subscribe"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "RENEW":
        return {
          buttonTitle: I18n.t("Renew"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "UPGRADE":
        return {
          buttonTitle:
            billingCycle === BillingCycle.LIFETIME
              ? I18n.t("Claim Founding Pass")
              : I18n.t("Upgrade"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "UPGRADE_TO_LATEST":
        return {
          buttonTitle: I18n.t("Switch to v2 Plan"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      default:
        return {
          buttonTitle: I18n.t("Contact Us"),
          buttonDisabled: false,
          isUpgrade: true,
        };
    }
  };

  const { buttonTitle, buttonDisabled, isUpgrade } = buttonText();
  const finalButtonTitle = buttonTitleOverride || buttonTitle;
  const finalButtonDisabled =
    typeof forceButtonDisabled === "boolean"
      ? forceButtonDisabled
      : buttonDisabled;

  const showUpgradeModal = useCallback(async () => {
    await UpgradeReminderModalCreateAble({
      scenario: "enterprise-realTimeReports",
    });
  }, []);

  const handleClick = useCallback(async () => {
    log.debug("[Member][debug][handleClick] isLoading", isLoading);
    if (isLoading || finalButtonDisabled || isAppPurchasing) {
      return;
    }
    // isUpgrade
    if (!isLoggedIn) {
      open?.();
      return;
    }
    if (isUpgrade) {
      showUpgradeModal();
      return;
    }

    // 触发购买的产品来自外部传入的 plan（或试用时的 trialPlan）
    let productForPurchase = plan;
    if (isTrialPlan && !isMember) {
      const trialPlan = getTrialData({
        trialList: trialList || [],
        id: plan?.id,
      });
      productForPurchase = trialPlan ?? plan;
    }
    log.debug("[Member][debug][pay]", JSON.stringify(productForPurchase));
    const backendProductId = productForPurchase?.id ?? null;
    log.debug("[Member][trace][pay] backendProductId", backendProductId);

    if (!backendProductId) {
      log.error("[Member][trace][pay] No product ID found");
      return;
    }
    if (!productForPurchase) {
      log.error("[Member][trace][pay] No product found for purchase");
      return;
    }

    const goStripe = () => {
      log.info("[Member][trace][pay] stripe click", backendProductId);
      onStripeClick?.(backendProductId);
    };

    if (shouldTryNativePayment) {
      beginAppPurchase();
      const result = await purchaseInApp(productForPurchase, {
        onSuccess: async () => {
          completeAppPurchase();
          log.debug(
            "[Member][trace][pay] onSuccess",
            onAppPurchaseSuccess,
            backendProductId,
          );
          await onAppPurchaseSuccess?.(backendProductId);
        },
        onMemberExist: async () => {
          completeAppPurchase();
          await invalidateMemberQueriesAfterDelay();
        },
        onError: (msg) => {
          completeAppPurchase();
          OspToast.error({
            content:
              msg || String(I18n.t("Purchase failed. Please try again.")),
          });
        },
        suppressTimeoutToast: true,
      });
      if (!result.success && result.fallbackToStripe) {
        goStripe();
      }
      return;
    }

    goStripe();
  }, [
    isLoading,
    finalButtonDisabled,
    isAppPurchasing,
    isLoggedIn,
    open,
    isUpgrade,
    showUpgradeModal,
    trialList,
    plan,
    onAppPurchaseSuccess,
    onStripeClick,
    shouldTryNativePayment,
    purchaseInApp,
    beginAppPurchase,
    completeAppPurchase,
    invalidateMemberQueriesAfterDelay,
    isTrialPlan,
    isMember,
  ]);
  const handleCryptoClick = () => {
    if (isLoading || buttonDisabled) {
      return;
    }
    if (!isLoggedIn) {
      open?.();
      return;
    }
    const payPath = `payment/${plan?.id}`;
    log.info("[Member][trace][pay] crypto click", plan?.id);
    router.push(payPath);
  };

  const actions = () => {
    // 旧流程（大屏/历史页面）沿用原有 crypto 展示条件。
    const shouldShowLegacyCrypto =
      showCryptoPayment &&
      !isUpgrade &&
      !(autoRenewal && isCurrentPlan) &&
      !isTrialPlan &&
      !!(!autoRenewal && billingCycle === BillingCycle.YEAR);
    // 新流程（移动端定价页）可传入矩阵结果覆盖展示，但仍受审核开关 showCryptoPayment 约束。
    const useCryptoOverride = typeof showCryptoOverride === "boolean";
    const shouldShowCrypto = useCryptoOverride
      ? showCryptoOverride && showCryptoPayment
      : shouldShowLegacyCrypto;

    if (customCta) {
      if (!isLoggedIn) {
        return (
          <div className={cn("flex flex-col gap-2")}>
            <button
              type="button"
              data-openreplay-label={`PurchaseActions · Button | ${buttonTitle}`}
              className={cn(
                "button md_button secondary_button w-full button_m",
              )}
              onClick={handleClick}
            >
              {buttonTitle}
            </button>
          </div>
        );
      }

      if (customCta.mode === "hidden") {
        return <div className="h-11" />;
      }

      const isCurrent = customCta.mode === "current";
      return (
        <div className="flex flex-col gap-2 w-full">
          <button
            type="button"
            data-openreplay-label={`PurchaseActions · Custom Button | ${customCta.text}`}
            className={cn(
              "button md_button w-full button_m",
              isCurrent ? "secondary_button" : "emphasis_button",
            )}
            disabled={isCurrent || customCta.disabled}
            onClick={customCta.onClick}
          >
            {customCta.text}
          </button>
          {/* {shouldShowCrypto ? (
            <div
              className={cn(
                "flex flex-row items-center justify-center body_m text-content_tertiary",
                (isCurrent || customCta.disabled || isLoading) &&
                  "text-content_tertiary/20",
              )}
            >
              <span>{I18n.t("or")}</span>
              <div
                data-openreplay-label={`PurchaseActions · div | ${
                  !autoRenewal && isCurrentPlan
                    ? I18n.t("Renew with crypto")
                    : I18n.t("Pay with crypto")
                }`}
                onClick={() => {
                  if (isCurrent || customCta.disabled || isLoading) {
                    return;
                  }
                  handleCryptoClick();
                }}
                className={cn(
                  "md:text-content_brand_primary text-content_accent4_primary hover:text-content_brand_primary/70 cursor-pointer button_m ml-1",
                  (isCurrent || customCta.disabled || isLoading) &&
                    "cursor-not-allowed md:text-content_brand_primary text-content_accent4_primary hover:md:text-content_brand_primary hover:text-content_accent4_primary",
                )}
              >
                {!autoRenewal && isCurrentPlan
                  ? I18n.t("Renew with crypto")
                  : I18n.t("Pay with crypto")}
              </div>
            </div>
          ) : null} */}
        </div>
      );
    }

    log.debug(
      "[文案] buttonTitle",
      finalButtonTitle,
      isFree,
      isUpgrade,
      !isUpgrade && !(autoRenewal && isCurrentPlan),
    );
    if (!isLoggedIn) {
      return (
        <div className={cn("flex flex-col gap-2")}>
          <button
            type="button"
            data-openreplay-label={`PurchaseActions · Button | ${buttonTitle}`}
            className={cn("button md_button secondary_button w-full button_m")}
            onClick={handleClick}
          >
            {buttonTitle}
          </button>
        </div>
      );
    }
    // 区分是免费还是付费
    return isFree ? (
      <div className={cn("flex flex-col gap-2 w-full h-11")}>
        {/* <button
          data-openreplay-label="PurchaseActions · Button"
          disabled
          className={cn(
            "w-full button tonal_button md_button button_m opacity-100",
          )}
        >
          {I18n.t("Current Plan")}
        </button>
        {!disableInvisiblePlaceholder && (
          <div
            className={cn(
              "h-5 invisible",
              !(!autoRenewal && billingCycle === BillingCycle.YEAR) && "hidden",
            )}
          ></div>
        )} */}
      </div>
    ) : (
      <div className="flex flex-col gap-2 w-full">
        <button
          data-openreplay-label="PurchaseActions · Button"
          onClick={handleClick}
          disabled={finalButtonDisabled}
          className={cn(
            "w-full button primary_button md_button button_m group",
            {
              tonal_button: finalButtonDisabled,
            },
          )}
          data-loading={(isLoading && isPayingPlan) || isAppPurchasing}
        >
          {!(isLoading && isPayingPlan) && !isAppPurchasing && (
            <div className="contents group-loading:hidden">
              {finalButtonTitle}
              {/* {!isInApp && !hidePaymentIcons && (
                <div
                  className={cn(
                    "items-center justify-center gap-[6px] ml-[6px] hidden",
                    {
                      flex: !isUpgrade && !isCurrentPlan,
                    },
                  )}
                >
                  <Image
                    src={getAssetsUrl("/assets/images/visa-dark.webp")}
                    alt="visa"
                    width={48}
                    height={14}
                  />
                  <Image
                    src={getAssetsUrl("/assets/images/applePay-dark.webp")}
                    alt="applepay"
                    width={23}
                    height={14}
                  />
                  <Image
                    src={getAssetsUrl("/assets/images/googlePay-dark.webp")}
                    alt="googlepay"
                    width={23}
                    height={14}
                  />
                </div>
              )} */}
            </div>
          )}
          <Icon
            name="ic_loading2"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
        {/* 如果试用会员，则不展示or后面的内容, 不能使用crypto购买 */}
        {shouldShowCrypto ? (
          <div
            className={cn(
              "flex flex-row items-center justify-center body_m text-content_tertiary",
              (finalButtonDisabled || isLoading) && "text-content_tertiary/20",
              !isH5 && finalButtonDisabled && !isCurrentPlan && "invisible",
              !useCryptoOverride &&
                isTrialPlan &&
                (disableInvisiblePlaceholder ? "hidden" : "invisible"),
              !useCryptoOverride &&
                !(!autoRenewal && billingCycle === BillingCycle.YEAR) &&
                "hidden",
            )}
          >
            <span>{I18n.t("or")}</span>
            <div
              data-openreplay-label={`PurchaseActions · div | ${
                !autoRenewal && isCurrentPlan
                  ? I18n.t("Renew with crypto")
                  : I18n.t("Pay with crypto")
              }`}
              onClick={handleCryptoClick}
              className={cn(
                "md:text-content_brand_primary text-content_accent4_primary hover:text-content_brand_primary/70 cursor-pointer button_m ml-1",
                (finalButtonDisabled || isLoading) &&
                  "cursor-not-allowed md:text-content_brand_primary text-content_accent4_primary hover:md:text-content_brand_primary hover:text-content_accent4_primary",
              )}
            >
              {!autoRenewal && isCurrentPlan
                ? I18n.t("Renew with crypto")
                : I18n.t("Pay with crypto")}
            </div>
          </div>
        ) : (
          <>
            {disableInvisiblePlaceholder ? null : (
              <div
                className={cn(
                  "h-5 invisible",
                  !(!autoRenewal && billingCycle === BillingCycle.YEAR) &&
                    "hidden",
                )}
              ></div>
            )}
          </>
        )}
      </div>
    );
  };

  if (isLoggedIn && !userPlanInfo) {
    return (
      <div className="flex flex-col md:border-b md:border-border_white_transparent10 md:pb-8">
        <div className={cn("flex flex-col gap-2 w-full")}>
          <button
            type="button"
            disabled
            data-loading={isLoading}
            className={cn("w-full button md_button button_m tonal_button")}
            aria-busy={isLoading}
          />
          {!disableInvisiblePlaceholder ? <div className="h-5" /> : null}
        </div>
      </div>
    );
  }

  return <div className={cn("flex flex-col", className)}>{actions()}</div>;
};

export default memo(PurchaseActions);
